import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { useShop, useServices } from "../lib/hooks";
import { createToken, isShopSuspended, getShopBySlug } from "../lib/api";
import { saveLastToken } from "../lib/lastToken";
import BrandHeader from "../components/BrandHeader";
import { FullPageLoader, ShopMissing, LoadError } from "../components/States";
import SuspendedGate from "../components/SuspendedGate";
import PWAInstallButton from "../components/PWAInstallButton";
import ResumeTokenCard from "../components/ResumeTokenCard";
import {
  User,
  UserRound,
  ArrowRight,
  Phone,
  Check,
  CheckCircle2,
  Tv,
  Ticket,
} from "lucide-react";

const AUTO_REDIRECT_SECONDS = 6;

export default function CustomerBookingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { shop, loading: shopLoading, error: shopError, refetch: refetchShop } = useShop(slug);
  const { services, loading: svcLoading, error: svcError } = useServices(shop?.id, { activeOnly: true });

  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [avatar, setAvatar] = useState("male");
  const [selected, setSelected] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  // After-booking success state: token object + countdown
  const [bookedToken, setBookedToken] = useState(null);
  const [countdown, setCountdown] = useState(AUTO_REDIRECT_SECONDS);

  useEffect(() => {
    if (services?.length && selected.size === 0) {
      setSelected(new Set([services[0].id]));
    }
  }, [services, selected.size]);

  // Post-booking countdown -> auto-redirect to Live TV display
  useEffect(() => {
    if (!bookedToken) return;
    if (countdown <= 0) {
      navigate(`/s/${slug}/tv`, { replace: true });
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [bookedToken, countdown, navigate, slug]);

  const totals = useMemo(() => {
    const sel = services.filter((s) => selected.has(s.id));
    const min = sel.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
    const price = sel.reduce((sum, s) => sum + Number(s.price || 0), 0);
    return { sel, min, price };
  }, [services, selected]);

  if (shopLoading || svcLoading) return <FullPageLoader label="Preparing booking" />;
  if (shopError) return <LoadError error={shopError} onRetry={refetchShop} />;
  if (svcError) return <LoadError error={svcError} onRetry={refetchShop} />;
  if (!shop) return <ShopMissing slug={slug} />;
  const suspended = isShopSuspended(shop);
  if (suspended) return <SuspendedGate shop={shop} reason={suspended.reason} />;

  // SUCCESS SCREEN — replaces the form after booking
  if (bookedToken) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0e0e0e] via-[#0a0a0a] to-[#050505] text-white">
        <Toaster theme="dark" position="top-center" richColors />
        <BrandHeader shop={shop} />
        <BookedSuccess
          shop={shop}
          slug={slug}
          token={bookedToken}
          countdown={countdown}
        />
      </div>
    );
  }

  const canSubmit =
    name.trim().length >= 2 &&
    whatsapp.trim().length >= 6 &&
    selected.size > 0 &&
    shop.is_open &&
    !submitting;

  function toggleService(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const fresh = await getShopBySlug(slug);
      if (!fresh || fresh.is_open === false) {
        toast.error("Shop is currently closed. Please try again later.");
        await refetchShop();
        setSubmitting(false);
        return;
      }
      const susp = isShopSuspended(fresh);
      if (susp) {
        toast.error("Service Temporarily Suspended. Contact AIBoxHub.");
        await refetchShop();
        setSubmitting(false);
        return;
      }
      const selSvcs = services.filter((s) => selected.has(s.id));
      const tok = await createToken(shop.id, {
        customer_name: name.trim(),
        whatsapp: whatsapp.trim(),
        avatar,
        service_id: selSvcs[0]?.id || null,
        service_name: selSvcs.map((s) => s.name).join(" + "),
      });
      toast.success(`Token #${tok.token_number} generated`);
      // Remember this token so the visitor can resume it later without booking again.
      saveLastToken(shop.slug, {
        tokenNumber: tok.token_number,
        whatsapp: tok.whatsapp,
      });
      // Show success screen with auto-redirect to live TV
      setBookedToken(tok);
      setCountdown(AUTO_REDIRECT_SECONDS);
    } catch (err) {
      toast.error(err.message || "Failed to generate token");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#101010] via-[#0a0a0a] to-[#050505] text-white">
      <Toaster theme="dark" position="top-center" richColors />
      <BrandHeader shop={shop} />

      <main className="px-5 md:px-10 pb-24 max-w-xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="font-numbers text-xs tracking-[0.4em] uppercase text-gold mb-4">
            Book Your Token
          </div>
          <h1 className="font-heading text-4xl md:text-5xl leading-tight tracking-tight text-white">
            Skip the wait.
            <br />
            <em className="not-italic italic text-gold">Reserve your spot.</em>
          </h1>

          {!shop.is_open && (
            <div
              className="mt-6 rounded-xl border border-red-400/50 bg-red-500/15 px-5 py-4 text-red-100"
              data-testid="shop-closed-banner"
            >
              <div className="font-numbers text-[11px] tracking-[0.3em] uppercase text-red-300 mb-1">
                Shop Closed
              </div>
              <div className="text-base">
                Shop is currently closed. Please try again later.
              </div>
            </div>
          )}
        </motion.div>

        {/* Already-booked-today shortcut — sits above the booking form */}
        <ResumeTokenCard shop={shop} />

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-10 rounded-2xl p-6 md:p-8 space-y-7 border border-gold/20"
          style={{
            background:
              "linear-gradient(180deg, rgba(28,28,28,0.92) 0%, rgba(18,18,18,0.92) 100%)",
            backdropFilter: "blur(24px) saturate(140%)",
            WebkitBackdropFilter: "blur(24px) saturate(140%)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 40px -8px rgba(230,198,98,0.18)",
          }}
          data-testid="booking-form"
        >
          <div>
            <label
              htmlFor="bf-name"
              className="font-numbers text-[12px] tracking-[0.3em] uppercase text-white/75"
            >
              Full Name
            </label>
            <input
              id="bf-name"
              data-testid="input-name"
              className="input-bright mt-2 text-base md:text-lg"
              placeholder="John Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
          </div>

          <div>
            <label
              htmlFor="bf-wa"
              className="font-numbers text-[12px] tracking-[0.3em] uppercase text-white/75"
            >
              WhatsApp Number
            </label>
            <div className="relative">
              <Phone
                className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-gold/80"
                strokeWidth={1.5}
              />
              <input
                id="bf-wa"
                data-testid="input-whatsapp"
                className="input-bright mt-2 pl-7 text-base md:text-lg"
                placeholder="+91 98765 43210"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                inputMode="tel"
              />
            </div>
          </div>

          <div>
            <div className="font-numbers text-[12px] tracking-[0.3em] uppercase text-white/75 mb-3">
              Avatar
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: "male", label: "Gentleman", Icon: User },
                { id: "female", label: "Lady", Icon: UserRound },
              ].map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setAvatar(id)}
                  data-testid={`avatar-${id}`}
                  className={`rounded-xl p-5 flex flex-col items-center gap-2 border-2 transition-all ${
                    avatar === id
                      ? "border-gold bg-gold/15 text-gold gold-glow"
                      : "border-white/20 text-white/80 hover:border-gold/50 hover:bg-white/5"
                  }`}
                >
                  <Icon className="w-8 h-8" strokeWidth={1.5} />
                  <div className="text-sm font-numbers tracking-[0.2em] uppercase">
                    {label}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="font-numbers text-[12px] tracking-[0.3em] uppercase text-white/75">
                Services · Tap to select
              </div>
              {totals.sel.length > 0 && (
                <div className="font-numbers text-[11px] tracking-[0.25em] uppercase text-gold">
                  {totals.sel.length} · {totals.min}m
                </div>
              )}
            </div>
            {services.length === 0 ? (
              <div className="text-sm text-white/60">
                No services available right now.
              </div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {services.map((s) => {
                  const isOn = selected.has(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleService(s.id)}
                      data-testid={`service-${s.id}`}
                      className={`w-full text-left rounded-xl p-4 border-2 transition-all flex items-center justify-between ${
                        isOn
                          ? "border-gold bg-gold/10 gold-glow"
                          : "border-white/15 hover:border-gold/40 hover:bg-white/5"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                            isOn ? "border-gold bg-gold text-void" : "border-white/40"
                          }`}
                        >
                          {isOn && <Check className="w-4 h-4" strokeWidth={3} />}
                        </div>
                        <div>
                          <div className="font-body text-base md:text-lg text-white">
                            {s.name}
                          </div>
                          <div className="text-xs text-white/55 font-numbers tracking-wider mt-0.5">
                            ~{s.duration_minutes} min
                          </div>
                        </div>
                      </div>
                      {Number(s.price) > 0 && (
                        <div className="font-numbers text-gold text-base">
                          ₹{Number(s.price).toLocaleString()}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {totals.sel.length > 0 && (
            <div className="rounded-xl bg-gold/10 border border-gold/30 px-4 py-3 flex items-center justify-between">
              <div className="text-xs font-numbers tracking-[0.25em] uppercase text-white/80">
                Total est. time
              </div>
              <div className="font-numbers text-lg text-gold tabular-nums">
                {totals.min}m{" "}
                {totals.price > 0 && (
                  <span className="text-white/70 text-xs ml-3">
                    ₹{totals.price.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          )}

          <button
            type="submit"
            data-testid="submit-booking"
            disabled={!canSubmit}
            className="btn-gold w-full flex items-center justify-center gap-2 text-base md:text-lg py-4"
          >
            {submitting ? "Generating…" : "Generate Token"}{" "}
            {!submitting && <ArrowRight className="w-5 h-5" />}
          </button>
        </motion.form>

        {/* PWA install — only on booking page */}
        <div className="mt-6">
          <PWAInstallButton shop={shop} />
        </div>
      </main>
    </div>
  );
}

// ============================================================
// Post-booking success screen
// ============================================================
function BookedSuccess({ shop, slug, token, countdown }) {
  return (
    <main className="px-5 md:px-10 pb-24 max-w-xl mx-auto" data-testid="booking-success">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="mt-6 rounded-2xl p-7 md:p-9 border border-gold/30 relative overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, rgba(35,28,12,0.6) 0%, rgba(18,18,18,0.92) 100%)",
          backdropFilter: "blur(24px) saturate(140%)",
          WebkitBackdropFilter: "blur(24px) saturate(140%)",
          boxShadow:
            "inset 0 1px 0 rgba(230,198,98,0.18), 0 12px 60px -10px rgba(230,198,98,0.32)",
        }}
      >
        <div className="absolute -top-28 -right-28 w-72 h-72 rounded-full bg-gold/15 blur-3xl" />

        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full border border-emerald-400/50 bg-emerald-400/15 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-emerald-400" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-numbers text-[11px] tracking-[0.35em] uppercase text-emerald-300">
              Booking Confirmed
            </div>
            <div className="font-heading text-2xl md:text-3xl mt-0.5 text-white">
              You&apos;re in the queue
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <div className="font-numbers text-[11px] tracking-[0.35em] uppercase text-white/60">
            Your Token
          </div>
          <div
            className="font-numbers font-semibold leading-none gold-text-glow mt-2 tabular-nums text-gold"
            style={{ fontSize: "clamp(5rem, 22vw, 9rem)" }}
            data-testid="success-token-number"
          >
            {token.token_number}
          </div>
          <div className="mt-3 font-heading text-xl text-white">
            {token.customer_name}
          </div>
          <div className="font-numbers text-xs tracking-[0.3em] uppercase text-white/55 mt-1">
            {token.service_name || "—"}
          </div>
        </div>

        <div className="mt-7 rounded-xl border border-gold/25 bg-gold/5 px-4 py-3 text-center text-sm text-white/80">
          Redirecting to{" "}
          <span className="text-gold font-semibold">Live TV Display</span> in{" "}
          <span className="font-numbers text-gold tabular-nums">{countdown}s</span>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to={`/s/${slug}/tv`}
            data-testid="cta-view-tv"
            className="btn-gold flex items-center justify-center gap-2 text-base py-3.5"
          >
            <Tv className="w-5 h-5" /> View Live Queue Display
          </Link>
          <Link
            to={`/s/${slug}/ticket/${token.token_number}`}
            data-testid="cta-view-ticket"
            className="rounded-full border-2 border-gold/40 hover:border-gold hover:bg-gold/10 transition-all px-5 py-3.5 flex items-center justify-center gap-2 text-base text-gold font-semibold"
          >
            <Ticket className="w-5 h-5" /> View My Live Token
          </Link>
        </div>

        <div className="mt-4 text-center text-[11px] font-numbers tracking-[0.3em] uppercase text-white/40">
          Powered by AIBox Global · AIBoxHub.in
        </div>
      </motion.div>

      <div className="mt-6">
        <PWAInstallButton shop={shop} />
      </div>
    </main>
  );
}
